import type { AdminProposalUserRole } from "../dto/admin-proposal.dto"
import type { EnfinTpoPricingRequestDto } from "../dto/enfin.dto"

export const queryKeys = {
  contracting: {
    package: (proposalId: string) =>
      [`contracting`, proposalId, `package`] as const,
  },
  publicPresentation: {
    printByToken: (token: string) =>
      [`print-solar-presentation`, token] as const,
    byToken: (token: string) => [`public-presentation`, token] as const,
    link: (proposalId: string) => [`presentation-link`, proposalId] as const,
    savings: (proposalId: string) =>
      [`public-presentation-savings`, proposalId] as const,
    lightReach: (proposalId: string) =>
      [`public-presentation-lightreach`, proposalId] as const,
    vpp: (proposalId: string | undefined) =>
      [`public-presentation-vpp`, proposalId] as const,
    batteryByToken: (token: string) =>
      [`public-battery-presentation`, token] as const,
    batterySavingsReportPrint: (token: string) =>
      [`print-battery-savings-report`, token] as const,
    batteryPrintByToken: (token: string) =>
      [`print-battery-presentation`, token] as const,
    solarV2ByToken: (token: string) =>
      [`public-solar-v2-presentation`, token] as const,
    solarV2PrintByToken: (token: string) =>
      [`print-solar-v2-presentation`, token] as const,
    financing: (token: string) =>
      [`public-presentation-financing`, token] as const,
    // Its own root, not nested under `financing`: a 409 invalidates the
    // financing read, and prefix matching would drag the rate-limited
    // disclosures call along with it.
    disclosures: (token: string) =>
      [`public-presentation-disclosures`, token] as const,
  },
  activeLoan: {
    all: [`active-loan`] as const,
    activityAll: [`active-loan-activity`] as const,
    byId: (activeLoanId: string | undefined) =>
      [`active-loan`, activeLoanId] as const,
    activity: (activeLoanId: string) =>
      [`active-loan-activity`, activeLoanId] as const,
    enfinSyncAll: [`enfin-active-loan-sync`] as const,
    stipulations: (activeLoanId: string) =>
      [`active-loan-stipulations`, activeLoanId] as const,
    stipulationsAll: [`active-loan-stipulations`] as const,
    enfinSync: (proposalId: string) =>
      [`enfin-active-loan-sync`, proposalId] as const,
  },
  activeStates: {
    all: [`active-states`] as const,
  },
  batteryProposal: {
    all: [`battery-proposal`] as const,
    byId: (proposalId: string) => [`battery-proposal`, proposalId] as const,
    savingsReport: (proposalId: string) =>
      [`battery-proposal`, proposalId, `savings-report`] as const,
    arbitrage: (proposalId: string) =>
      [`battery-proposal`, proposalId, `arbitrage`] as const,
    inputs: (proposalId: string) =>
      [`battery-proposal`, proposalId, `inputs`] as const,
    // Account candidates belong to the Site. Refresh them explicitly after a
    // utility save, independently of ordinary proposal edits.
    genabilityAccounts: [`battery-proposal-genability-account`] as const,
    genabilityAccount: (proposalId: string) =>
      [`battery-proposal-genability-account`, proposalId] as const,
    utilityCompanies: (proposalId: string) =>
      [`battery-proposal-utility-companies`, proposalId] as const,
    utilityTariffs: (proposalId: string, lseId: number | null) =>
      [`battery-proposal-utility-tariffs`, proposalId, lseId] as const,
    pricing: (proposalId: string) =>
      [`battery-proposal`, proposalId, `pricing`] as const,
    incentives: (proposalId: string) =>
      [`battery-proposal`, proposalId, `incentives`] as const,
    vppPrograms: (proposalId: string) =>
      [`battery-proposal`, proposalId, `vpp-programs`] as const,
    extendedWarranties: (proposalId: string) =>
      [`battery-proposal`, proposalId, `extended-warranties`] as const,
    compute: (proposalId: string) =>
      [`battery-proposal`, proposalId, `compute`] as const,
    // Nested under the compute read so a stale-options failure refetches both.
    computePreview: (proposalId: string) =>
      [`battery-proposal`, proposalId, `compute`, `preview`] as const,
    financialSummary: (proposalId: string) =>
      [`battery-proposal`, proposalId, `financial-summary`] as const,
    contract: (proposalId: string) =>
      [`battery-proposal`, proposalId, `contract`] as const,
    financing: (proposalId: string) =>
      [`battery-proposal`, proposalId, `financing`] as const,
    // Nested under financing so anything that invalidates the financing read
    // re-reads the offers that describe the same selection.
    concertOffers: (proposalId: string) =>
      [`battery-proposal`, proposalId, `financing`, `concert-offers`] as const,
    lightReachOffers: (proposalId: string) =>
      [
        `battery-proposal`,
        proposalId,
        `financing`,
        `lightreach-offers`,
      ] as const,
    lightReachApplicationDisclosures: (proposalId: string) =>
      [
        `battery-proposal`,
        proposalId,
        `lightreach-application`,
        `disclosures`,
      ] as const,
    concertApplication: (proposalId: string) =>
      [`battery-proposal`, proposalId, `concert-application`] as const,
    // Nested under the Concert application so a loan-document send, which is
    // what starts the supplemental package, re-reads both.
    concertSupplemental: (proposalId: string) =>
      [
        `battery-proposal`,
        proposalId,
        `concert-application`,
        `supplemental-documents`,
      ] as const,
    sungageApplication: (proposalId: string, applicationId: string) =>
      [
        `battery-proposal`,
        proposalId,
        `sungage-application`,
        applicationId,
      ] as const,
    sungageApplications: (proposalId: string) =>
      [`battery-proposal`, proposalId, `sungage-application`] as const,
    equipmentPreview: (
      proposalId: string,
      draft: {
        batterySystemType: string
        financeType?: string | null
        fundingGroup?: string | null
        loanProgramId?: number | null
      },
    ) =>
      [
        `battery-proposal`,
        proposalId,
        `equipment-preview`,
        draft.batterySystemType,
        draft.financeType ?? null,
        draft.fundingGroup ?? null,
        draft.loanProgramId ?? null,
      ] as const,
    equipmentPreviews: (proposalId: string) =>
      [`battery-proposal`, proposalId, `equipment-preview`] as const,
    debugInputs: (proposalId: string, inputType = `detail`) =>
      [`battery-proposal`, proposalId, `debug`, `inputs`, inputType] as const,
    presentationLink: (proposalId: string) =>
      [`battery-proposal`, proposalId, `presentation-link`] as const,
    presentationViews: (proposalId: string) =>
      [`battery-proposal`, proposalId, `presentation-link`, `views`] as const,
  },
  enfinSupplementalRecovery: (proposalId: string) =>
    [`enfinSupplementalRecovery`, proposalId] as const,
  adminProposals: {
    all: [`admin-proposals`] as const,
    createData: [`admin-proposals`, `create-data`] as const,
    userOptions: (role: AdminProposalUserRole, search: string) =>
      [`admin-proposals`, `user-options`, role, search] as const,
    enterpriseOptions: (search: string) =>
      [`admin-proposals`, `enterprise-options`, search] as const,
    lists: [`admin-proposals`, `list`] as const,
    list: <TInput>(input: TInput) => [`admin-proposals`, `list`, input] as const,
    mockAddress: (state: string) =>
      [`admin-proposals`, `mock-address`, state] as const,
    designInverters: (proposalId: string) =>
      [`admin-proposals`, `design-inverters`, proposalId] as const,
  },
  adminIncentives: {
    all: [`admin-incentives`] as const,
    list: <TInput>(input: TInput) => [`admin-incentives`, input] as const,
    detail: (incentiveId: string) =>
      [`admin-incentives`, `detail`, incentiveId] as const,
    batteryManufacturers: (search: string, take: number) =>
      [`admin-incentives`, `battery-manufacturers`, search, take] as const,
  },
  pricingHistory: {
    all: [`pricing-history`] as const,
    list: <TInput>(proposalId: string, input: TInput) =>
      [`pricing-history`, proposalId, `list`, input] as const,
    comparison: (proposalId: string, snapshotId: string) =>
      [`pricing-history`, proposalId, `comparison`, snapshotId] as const,
  },
  adminFinancing: {
    all: [`admin-financing`] as const,
    loanProductsAll: [`admin-financing`, `loan-products`] as const,
    loanProducts: <TInput>(input: TInput) =>
      [`admin-financing`, `loan-products`, input] as const,
    loanProduct: (loanProductId: string) =>
      [`admin-financing`, `loan-products`, loanProductId] as const,
    partners: [`admin-financing`, `partners`] as const,
  },
  adminUsers: {
    all: [`admin-users`] as const,
    byId: (userId: string) => [`admin-users`, userId] as const,
    list: <TInput>(input: TInput) => [`admin-users`, input] as const,
    permissions: [`admin-users`, `permissions`] as const,
  },
  arcadiaGreenButton: {
    all: [`arcadia-green-button`] as const,
    site: (siteId: string) => [`arcadia-green-button`, siteId] as const,
    proposal: (siteId: string, proposalId: string) =>
      [`arcadia-green-button`, siteId, proposalId] as const,
    diagnostic: (siteId: string, connectionId: string) =>
      [
        `arcadia-green-button`,
        `support-diagnostic`,
        siteId,
        connectionId,
      ] as const,
  },
  projectTrendReports: {
    overview: <TQuery>(query: TQuery) =>
      [`project-trend-reports`, `overview`, query] as const,
  },
  aiComputeReports: {
    all: [`ai-compute-reports`] as const,
    filterOptions: [`ai-compute-reports`, `filter-options`] as const,
    overview: <TQuery>(query: TQuery) =>
      [`ai-compute-reports`, `overview`, query] as const,
  },
  batteryReports: {
    all: [`battery-reports`] as const,
    filterOptions: [`battery-reports`, `filter-options`] as const,
    report: <TQuery>(report: string, query: TQuery) =>
      [`battery-reports`, report, query] as const,
  },
  concert: {
    all: [`concert`] as const,
    additionalDocumentsAll: [`concert-additional-documents`] as const,
    additionalDocuments: (proposalId: string) =>
      [`concert-additional-documents`, proposalId] as const,
    adminUsers: <TQuery>(query: TQuery) =>
      [`concert-admin-users`, query] as const,
    changeOrderInfo: (proposalId: string | null) =>
      [`concert-change-order-info`, proposalId] as const,
    changeOrderInfoAll: [`concert-change-order-info`] as const,
    loanDocsReadiness: (proposalId: string) =>
      [`concert-loan-docs-readiness`, proposalId] as const,
    loanDocsReadinessAll: [`concert-loan-docs-readiness`] as const,
    pricing: (proposalId: string) => [`concert`, proposalId, `pricing`] as const,
    userStatus: (proposalId: string) =>
      [`concert-user-status`, proposalId] as const,
    userStatuses: [`concert-user-status`] as const,
  },
  contractTabLabels: {
    all: [`contract-tab-labels`] as const,
  },
  design: {
    all: [`design`] as const,
  },
  enfin: {
    all: [`enfin`] as const,
    tpoPricings: (proposalId: string) =>
      [`enfin`, `tpo-pricing`, proposalId] as const,
    tpoPricing: (
      proposalId: string,
      product?: EnfinTpoPricingRequestDto | null,
    ) =>
      [
        `enfin`,
        `tpo-pricing`,
        proposalId,
        product?.term ?? null,
        product?.productName ?? null,
        product?.escalatorRate ?? null,
      ] as const,
    tpoProducts: (proposalId: string) =>
      [`enfin`, `tpo-products`, proposalId] as const,
  },
  enterprises: {
    all: [`enterprises`] as const,
  },
  equipment: {
    all: [`equipment`] as const,
    moduleInverterCompatibility: (proposalId: string) =>
      [`equipment`, `module-inverter-compatibility`, proposalId] as const,
    externalIdentifiers: <TInput>(input: TInput) =>
      [`equipment-external-identifiers`, input] as const,
    externalIdentifierLists: [`equipment-external-identifiers`] as const,
    syncStatus: [`equipment`, `sync`, `status`] as const,
  },
  externalProviderCallReport: {
    all: [`external-provider-call-report`] as const,
    report: <TFilters>(filters: TFilters) =>
      [`external-provider-call-report`, filters] as const,
  },
  financeConfig: {
    types: [`proposal-finance-config`, `types`] as const,
  },
  financeProduct: {
    all: [`finance-product`] as const,
  },
  fundingGroups: {
    all: [`funding-groups`] as const,
  },
  genability: {
    intelligentBaseline: (accountId: string, inputHash?: string) =>
      [
        `genability`,
        `intelligent-baseline`,
        accountId,
        ...(inputHash ? [inputHash] : []),
      ] as const,
  },
  imagery: {
    savedImageAvailability: (imageUrl: string | null | undefined) =>
      [`imagery`, `saved-image-availability`, imageUrl] as const,
  },
  lightReach: {
    all: [`light-reach`] as const,
    availableAdders: (proposalId: string) =>
      [`light-reach`, proposalId, `available-adders`] as const,
    byProposal: (proposalId: string) => [`light-reach`, proposalId] as const,
    contracts: (proposalId: string) =>
      [`light-reach`, proposalId, `contracts`] as const,
    creditApplication: (proposalId: string) =>
      [`light-reach`, proposalId, `credit-application`] as const,
    disclosures: [`light-reach`, `disclosures`] as const,
    pricing: (proposalId: string) =>
      [`light-reach`, proposalId, `pricing`] as const,
    quote: (proposalId: string) => [`light-reach`, proposalId, `quote`] as const,
    systemDesign: (proposalId: string) =>
      [`light-reach`, proposalId, `system-design`] as const,
    validateChangeOrder: (proposalId: string, production: number | undefined) =>
      [`light-reach`, proposalId, `validate-change-order`, production] as const,
    validateChangeOrders: (proposalId: string) =>
      [`light-reach`, proposalId, `validate-change-order`] as const,
  },
  loanProductPartners: {
    all: [`loan-product-partners`] as const,
    byProposal: (proposalId: string | undefined) =>
      [`proposal-loan-product-partners`, proposalId] as const,
  },
  proposal: {
    all: [`proposal`] as const,
    byId: (proposalId: string) => [`proposal`, proposalId] as const,
    debugRequestInput: (proposalId: string, inputType: string) =>
      [`proposal-debug-request-input`, proposalId, inputType] as const,
    debugRequestInputsAll: [`proposal-debug-request-input`] as const,
    details: (proposalId: string) =>
      [`proposal`, proposalId, `details`] as const,
    equipmentPreview: (
      proposalId: string,
      draft: {
        financeType: string
        fundingGroup: string | null
        loanProgramId: number | null
      },
    ) =>
      [
        `proposal`,
        proposalId,
        `equipment-preview`,
        draft.financeType,
        draft.fundingGroup ?? null,
        draft.loanProgramId ?? null,
      ] as const,
    equipmentPreviews: (proposalId: string) =>
      [`proposal`, proposalId, `equipment-preview`] as const,
    pricingInvalidationLegacyWrapper: (proposalId: string) =>
      [[`proposal`, proposalId, `pricing`] as const] as const,
    pricing: (proposalId: string) =>
      [`proposal`, proposalId, `pricing`] as const,
    pricingComparisons: (proposalId: string, requestedMargins: number[] = []) =>
      [
        `proposal`,
        proposalId,
        `pricing-comparisons`,
        ...requestedMargins,
      ] as const,
    savings: (proposalId: string) =>
      [`proposal`, proposalId, `savings`] as const,
    views: (proposalId: string) => [`proposal-views`, proposalId] as const,
  },
  proposalConsumption: {
    all: [`proposal-consumption`] as const,
    selection: (proposalId: string) =>
      [`proposal-consumption`, proposalId, `selection`] as const,
    insights: (proposalId: string) =>
      [`proposal-consumption`, proposalId, `insights`] as const,
    insightsForImport: (proposalId: string, importId: string | null) =>
      [`proposal-consumption`, proposalId, `insights`, importId] as const,
  },
  proposalDesign: {
    all: [`proposal-designs`] as const,
    active: (proposalId: string) =>
      [`proposal-designs`, proposalId, `active`] as const,
    activeAurora: (proposalId: string) =>
      [`proposal-designs`, proposalId, `aurora`, `active`] as const,
    auroraProjectDesigns: (proposalId: string) =>
      [`proposal-designs`, proposalId, `aurora`, `project-designs`] as const,
  },
  proposalLoanPrograms: {
    byProposal: (proposalId: string | undefined) =>
      [`proposal-loan-programs`, proposalId] as const,
  },
  proposalUtility: {
    options: (proposalId: string) =>
      [`proposal-utility`, proposalId, `options`] as const,
  },
  proposalVppIncentives: {
    byProposal: (proposalId: string | undefined) =>
      [`proposal-vpp-incentives`, proposalId] as const,
    eligible: (proposalId: string | undefined) =>
      [`proposal-vpp-incentives-eligible`, proposalId] as const,
  },
  repPrograms: {
    all: [`rep-programs`] as const,
    byId: (programId: string) => [`rep-program`, programId] as const,
    list: <TInput>(input: TInput) => [`rep-programs`, input] as const,
    utilityCompanies: (search: string, take: number) =>
      [`rep-program-utility-companies`, search, take] as const,
    versions: (programId: string) =>
      [`rep-program-versions`, programId] as const,
  },
  solarProposal: {
    all: [`solar-proposal`] as const,
    byId: (proposalId: string) => [`solar-proposal`, proposalId] as const,
    design: (proposalId: string) =>
      [`solar-proposal`, proposalId, `design`] as const,
    designVersions: (proposalId: string) =>
      [`solar-proposal`, proposalId, `design`, `versions`] as const,
    designer: (proposalId: string) =>
      [`solar-proposal`, proposalId, `designer`] as const,
    equipment: (proposalId: string) =>
      [`solar-proposal`, proposalId, `equipment`] as const,
    impact: (proposalId: string) =>
      [`solar-proposal`, proposalId, `impact`] as const,
    savings: (proposalId: string) =>
      [`solar-proposal`, proposalId, `savings`] as const,
    pricing: (proposalId: string) =>
      [`solar-proposal`, proposalId, `pricing`] as const,
    /** The Share drawer's readiness and link: it reads pricing and savings freshness. */
    share: (proposalId: string) =>
      [`solar-proposal`, proposalId, `share`] as const,
    shareViews: (proposalId: string) =>
      [`solar-proposal`, proposalId, `share`, `views`] as const,
    systemPricing: (proposalId: string) =>
      [`solar-proposal`, proposalId, `system-pricing`] as const,
    utility: (proposalId: string) =>
      [`solar-proposal`, proposalId, `utility`] as const,
    consumption: (proposalId: string) =>
      [`solar-proposal`, proposalId, `consumption`] as const,
    aurora: (proposalId: string) =>
      [`solar-proposal`, proposalId, `aurora`] as const,
    auroraProjectDesigns: (proposalId: string) =>
      [`solar-proposal`, proposalId, `aurora`, `project-designs`] as const,
    // Outside the `aurora` prefix: a link invalidates that prefix, and must
    // not ask Aurora to preview the design it just linked.
    auroraLinkPreview: (proposalId: string, ref: string) =>
      [`solar-proposal`, proposalId, `aurora-link-preview`, ref] as const,
    productOffering: (proposalId: string) =>
      [`solar-proposal`, proposalId, `product-offering`] as const,
    compute: (proposalId: string) =>
      [`solar-proposal`, proposalId, `compute`] as const,
    /** Credit Human Loan Products, the selection and its Quote, which pricing writes. */
    financing: (proposalId: string) =>
      [`solar-proposal`, proposalId, `financing`] as const,
    computePreview: (proposalId: string) =>
      [`solar-proposal`, proposalId, `compute`, `preview`] as const,
    /** The Concert offers Refresh Pricing stored, and the saved one. */
    concertOffers: (proposalId: string) =>
      [`solar-proposal`, proposalId, `concert-offers`] as const,
    /** The seller's Concert user (a live Concert read). */
    concertSellerReadiness: (proposalId: string) =>
      [`solar-proposal`, proposalId, `concert-seller-readiness`] as const,
    /** The Concert application status, reconciled on read. */
    concertApplication: (proposalId: string) =>
      [`solar-proposal`, proposalId, `concert-application`] as const,
    /** The stored Sungage application and Start's gate (no Sungage call). */
    sungageApplication: (proposalId: string) =>
      [`solar-proposal`, proposalId, `sungage-application`] as const,
    /** The LightReach step's one read: offers, selection, application and contract. */
    lightReach: (proposalId: string) =>
      [`solar-proposal`, proposalId, `lightreach`] as const,
    /** The disclosures the LightReach application shows, as published now. */
    lightReachDisclosures: (proposalId: string) =>
      [`solar-proposal`, proposalId, `lightreach`, `disclosures`] as const,
    summary: (proposalId: string) =>
      [`solar-proposal`, proposalId, `summary`] as const,
    adders: (proposalId: string) =>
      [`solar-proposal`, proposalId, `adders`] as const,
    rebates: (proposalId: string) =>
      [`solar-proposal`, proposalId, `rebates`] as const,
    extendedWarranties: (proposalId: string) =>
      [`solar-proposal`, proposalId, `extended-warranties`] as const,
    incentives: (proposalId: string) =>
      [`solar-proposal`, proposalId, `incentives`] as const,
    vppPrograms: (proposalId: string) =>
      [`solar-proposal`, proposalId, `vpp-programs`] as const,
    // Read only while the Contract drawer is open; every price- or
    // savings-dependent Solar command invalidates it.
    contract: (proposalId: string) =>
      [`solar-proposal`, proposalId, `contract`] as const,
    // Read only while Contract › HDM is open; it calls HDM for the draft.
    hdm: (proposalId: string) => [`solar-proposal`, proposalId, `hdm`] as const,
    productOfferingPreview: (
      proposalId: string,
      draft: {
        financeType: string
        loanProgramId: number | null
        fundingGroup?: string | null
      },
    ) =>
      [
        `solar-proposal`,
        proposalId,
        `product-offering`,
        `preview`,
        draft.financeType,
        draft.loanProgramId,
        // A Sungage group asks PowurOS with its own funding type.
        draft.fundingGroup ?? null,
      ] as const,
  },
  solarExplorer: {
    building: (lat: number, lng: number) =>
      [`solar-explorer`, `building`, lat, lng] as const,
    layers: (lat: number, lng: number) =>
      [`solar-explorer`, `layers`, lat, lng] as const,
  },
  serviceIntegrations: {
    all: [`service-integrations`] as const,
    byId: (integrationId: string) =>
      [`service-integrations`, integrationId] as const,
    list: <TInput>(input: TInput) => [`service-integrations`, input] as const,
  },
  user: {
    all: [`user`] as const,
    license: (userId: string, licenseId: string) =>
      [`user`, userId, `licenses`, licenseId] as const,
    licenses: (userId: string) => [`user`, userId, `licenses`] as const,
    profile: () => [`user`, `profile`] as const,
  },
  vppPrograms: {
    all: [`vpp-programs`] as const,
    byId: (programId: string | undefined) => [`vpp-program`, programId] as const,
    eligible: (proposalId: string) =>
      [`proposal-vpp-incentives-eligible`, proposalId] as const,
    list: <TInput>(input: TInput) => [`vpp-programs`, input] as const,
    manufacturers: (search: string, take: number) =>
      [`vpp-manufacturers`, search, take] as const,
  },

  // Legacy GraphQL-bridge keys. Delete these with their GraphQL-fed reads.
  legacy: {
    graphql: {
      baseEquipmentForProposal: [`GetBaseEquipmentForProposal`] as const,
      design: [`Design`] as const,
      equipmentForProposal: [`GetEquipmentForProposal`] as const,
      proposal: [`GetProposal`] as const,
      quotesForProposal: [`GetQuotesForProposal`] as const,
      selectIncentivesModal: (proposalId: string) =>
        [`GetSelectIncentivesModal`, { proposalId }] as const,
      warrantyPricing: (proposalId: string) =>
        [`GetWarrantyPricing`, { proposalId }] as const,
    },
  },
} as const

export const PROPOSAL_KEYS = {
  all: queryKeys.proposal.all,
  details: queryKeys.proposal.details,
  equipmentPreview: queryKeys.proposal.equipmentPreview,
  equipmentPreviews: queryKeys.proposal.equipmentPreviews,
  pricing: queryKeys.proposal.pricing,
  pricingComparisons: queryKeys.proposal.pricingComparisons,
} as const

export const PROPOSAL_DESIGN_KEYS = {
  all: queryKeys.proposalDesign.all,
  activeAurora: queryKeys.proposalDesign.activeAurora,
} as const

export const CONCERT_KEYS = {
  all: queryKeys.concert.all,
  pricing: queryKeys.concert.pricing,
} as const

export const ENFIN_KEYS = {
  all: queryKeys.enfin.all,
  tpoPricing: queryKeys.enfin.tpoPricing,
  tpoProducts: queryKeys.enfin.tpoProducts,
} as const

export const USER_KEYS = {
  all: queryKeys.user.all,
  profile: queryKeys.user.profile,
} as const

export const UTILITY_KEYS = {
  all: [`proposal-utility`] as const,
  options: queryKeys.proposalUtility.options,
} as const
